import type { GradeSummary } from '../supabase/analytics';
import type { GradeStats } from './types';

/**
 * Trigger a browser file download from a Blob.
 * Follows the same pattern as exportToCSV in analytics.ts.
 */
export function downloadBlob(blob: Blob, filename: string): void {
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Format a date for document headers/footers.
 */
export function formatReportDate(date?: Date): string {
  const d = date || new Date();
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * Sanitize text for document generation (strip HTML, handle null).
 */
export function sanitizeText(text: string | null | undefined): string {
  if (!text) return '';
  return text
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .trim();
}

/**
 * Calculate summary statistics from a GradeSummary array.
 */
export function calculateGradeStats(students: GradeSummary[]): GradeStats {
  if (students.length === 0) {
    return { classAverage: 0, highest: 0, lowest: 0, totalStudents: 0, passingCount: 0 };
  }

  const percentages = students.map((s) => s.percentage);
  const classAverage = percentages.reduce((a, b) => a + b, 0) / percentages.length;
  const highest = Math.max(...percentages);
  const lowest = Math.min(...percentages);
  const passingCount = percentages.filter((p) => p >= 70).length;

  return {
    classAverage: Math.round(classAverage * 10) / 10,
    highest,
    lowest,
    totalStudents: students.length,
    passingCount,
  };
}

/**
 * Get a letter grade from a percentage.
 */
export function getLetterGrade(percentage: number): string {
  if (percentage >= 90) return 'A';
  if (percentage >= 80) return 'B';
  if (percentage >= 70) return 'C';
  if (percentage >= 60) return 'D';
  return 'F';
}
